import React from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';

import { FormattedNumberTextField } from '../../common/FormattedNumberTextField';
import { LLMModelFormValues } from '../../../types/admin';
import { LLMModelPriceField, modelPriceFields } from './types';

export interface ModelTokenPricingSectionProps {
  values: LLMModelFormValues;
  onValueChange: <K extends keyof LLMModelFormValues>(
    field: K,
    value: LLMModelFormValues[K]
  ) => void;
}

const hasValue = (value: unknown): boolean => value !== null && value !== undefined;

export const ModelTokenPricingSection: React.FC<ModelTokenPricingSectionProps> = ({
  values,
  onValueChange,
}) => {
  const [draftField, setDraftField] = React.useState<LLMModelPriceField | ''>('');
  const [draftValue, setDraftValue] = React.useState<number | undefined>(undefined);

  const activeFields = modelPriceFields.filter((item) => hasValue(values[item.field]));
  const availableFields = modelPriceFields.filter((item) => !hasValue(values[item.field]));

  const setPrice = (field: LLMModelPriceField, value: number | null) =>
    onValueChange(field, value as never);

  const addPrice = () => {
    if (!draftField || draftValue === undefined) return;
    setPrice(draftField, draftValue);
    setDraftField('');
    setDraftValue(undefined);
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        className="mb-3 font-semibold text-gray-700 dark:text-slate-300"
      >
        Token Pricing
      </Typography>

      <Box className="space-y-3">
        {activeFields.map((item) => (
          <Box
            key={item.field}
            className="grid grid-cols-1 items-center gap-3 rounded border border-slate-200 p-3 dark:border-slate-700 md:grid-cols-[2fr_2fr_auto]"
          >
            <Typography variant="body2" className="text-gray-700 dark:text-slate-300">
              {item.label}
            </Typography>
            <FormattedNumberTextField
              fullWidth
              size="small"
              label="Price"
              value={values[item.field]}
              integer={false}
              allowEmpty
              onValueChange={(value) => setPrice(item.field, value ?? null)}
              inputProps={{ min: 0, step: 0.00000001 }}
            />
            <IconButton
              aria-label={`Remove ${item.label}`}
              onClick={() => setPrice(item.field, null)}
            >
              <Trash2 size={18} />
            </IconButton>
          </Box>
        ))}
      </Box>

      {availableFields.length > 0 && (
        <Box className="mt-3 grid grid-cols-1 items-center gap-3 md:grid-cols-[2fr_2fr_auto]">
          <TextField
            select
            fullWidth
            size="small"
            label="Price type"
            value={draftField}
            onChange={(event) => setDraftField(event.target.value as LLMModelPriceField)}
          >
            {availableFields.map((item) => (
              <MenuItem key={item.field} value={item.field}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <FormattedNumberTextField
            fullWidth
            size="small"
            label="Value"
            value={draftValue}
            integer={false}
            allowEmpty
            onValueChange={setDraftValue}
            inputProps={{ min: 0, step: 0.00000001 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            disabled={!draftField || draftValue === undefined}
            onClick={addPrice}
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
};
